import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { Address, isThor, Signer } from "rujira.js";
import { AccountProvider, Provider, wallets } from "rujira.ui";
import { useAccountLayer } from "./accountLayer";
import { findSelectedEntry, pickSignerProvider } from "./walletSelection";
const {
  addProviderKey,
  clearProviderKeys,
  clearSelected,
  loadProviderKeys,
  loadSelected,
  removeProviderKey,
  saveSelected,
} = wallets;

const ERROR = () => {
  throw new Error("AccountProvider Context not defined");
};

const Context = createContext<AccountProvider>({
  accounts: undefined,
  signer: ERROR,
  select: ERROR,
  connect: ERROR,
  disconnect: ERROR,
  disconnectAll: ERROR,
  isAvailable: ERROR,
});

/** The selected account; `owner` is the wallet an app-layer account belongs to. */
export type SelectedAccount = NonNullable<AccountProvider["selected"]> & {
  owner?: string;
};

export type Accounts = Omit<AccountProvider, "selected"> & {
  selected?: SelectedAccount;
};

// Currently we only support 1-1 relationship between providers and connected account(s)
// If for example a user has multiple accounts connected on a Metamask wallet, the context
// will provide just the one that is currently selected
// This is relatively easily extended to allow a user to connect to a provider multiple
// times, registering each account/address and allowing the UI to visually represent
// (or auto-fix) a mis-match between UI-selected account and provider-selected account
const storedSelected = loadSelected();
// These are the providers that have been previously connected, and so we should attempt to re-connect
// on a page refresh, where possible without triggering annoying modal prompts from the wallets
// If a connection is refused, it should be removed from the list of stored providers
const connectedProviders: Provider.Key[] = loadProviderKeys();

type State = Partial<Record<Provider.Key, Address[]>>;

const connectionError = (cause: unknown, key: Provider.Key): Error =>
  cause instanceof Error && cause.message
    ? cause
    : new Error(
        typeof cause === "string" && cause.trim()
          ? cause
          : `Unable to connect ${key}. Unlock your wallet and try again.`
      );

const insertResult = (
  agg: State,
  v: PromiseSettledResult<{ p: Provider.Key; x: Address[] }>
): State => {
  return v.status === "fulfilled"
    ? {
        ...agg,
        [v.value.p]: v.value.x,
      }
    : agg;
};

export const AccountsContext: FC<PropsWithChildren> = ({ children }) => {
  const [selectedAddress, setSelectedAddress] = useState<string | undefined>(
    storedSelected?.address
  );
  const [selectedProvider, setSelectedProvider] = useState<
    Provider.Key | undefined
  >(storedSelected?.provider);

  const [addresses, setAddresses] = useState<State>({});
  // Connection promises may finish after another wallet was selected. Keep
  // commits synchronous here so late results never use their starting render.
  const latest = useRef({ addresses, selectedAddress });
  const manuallyTouched = useRef(new Set<Provider.Key>());
  const updateAddresses = (update: (previous: State) => State) => {
    const next = update(latest.current.addresses);
    latest.current.addresses = next;
    setAddresses(next);
  };

  useEffect(() => {
    let active = true;
    Promise.allSettled(
      connectedProviders
        .filter((p) => p !== "Vultisig")
        .map((p) => {
          return Provider.signer(p)
            .connect()
            .then((x) => ({ p, x }))
            .catch((err) => {
              // Don't keep trying
              if (active && !manuallyTouched.current.has(p))
                removeProviderKey(p);
              throw err;
            });
        })
    ).then((results) => {
      if (!active) return;
      const restored = results
        .filter(
          (result) =>
            result.status !== "fulfilled" ||
            !manuallyTouched.current.has(result.value.p)
        )
        .reduce(insertResult, {});
      updateAddresses((previous) => ({ ...restored, ...previous }));
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let listening = true;
    Object.keys(addresses).forEach((x) => {
      const provider = x as Provider.Key;
      const signer = Provider.signer(provider);
      signer.onChange?.(() => {
        if (!listening) return;
        const previous = latest.current.addresses[provider];
        signer
          .connect()
          .then((connected) => {
            if (latest.current.addresses[provider] !== previous) return;
            if (connected.length === 0)
              throw new Error(
                `No accounts were returned by ${provider}. Unlock your wallet and reconnect.`
              );
            const thorAccount = connected.find(isThor);
            const next = { ...latest.current.addresses, [provider]: connected };
            const selectionIsConnected = Object.values(next).some((accounts) =>
              accounts?.some(
                (account) => account.address === latest.current.selectedAddress
              )
            );
            if (!selectionIsConnected)
              select(
                thorAccount ? { address: thorAccount, provider } : undefined
              );
            updateAddresses(() => next);
          })
          .catch((cause: unknown) => {
            // A manual reconnect/disconnect takes precedence over an old event.
            if (latest.current.addresses[provider] !== previous) return;
            const next: State = { ...latest.current.addresses };
            delete next[provider];
            if (
              !Object.values(next).some((accounts) =>
                accounts?.some(
                  (account) =>
                    account.address === latest.current.selectedAddress
                )
              )
            )
              select();
            updateAddresses(() => next);
            toast.error(connectionError(cause, provider).message);
          });
      });
    });
    return () => {
      listening = false;
    };
  }, [addresses]);

  const connect = async (key: Provider.Key) => {
    manuallyTouched.current.add(key);
    try {
      const signer = Provider.signer(key);
      const connected = await signer.connect();
      if (connected.length === 0) {
        throw new Error(
          `No accounts were returned by ${key}. Unlock your wallet and approve the connection, then try again.`
        );
      }
      addProviderKey(key);
      const next = { ...latest.current.addresses, [key]: connected };
      const selectionIsConnected = Object.values(next).some((accounts) =>
        accounts?.some(
          (account) => account.address === latest.current.selectedAddress
        )
      );
      const thorAccount = connected.find(isThor);
      if (!selectionIsConnected && thorAccount)
        select({ address: thorAccount, provider: key });
      updateAddresses(() => next);
    } catch (cause: unknown) {
      const error = connectionError(cause, key);
      toast.error(error.message);
      throw error;
    }
  };

  const select = (
    account?: { address: Address; provider: Provider.Key } | null
  ) => {
    if (!account) {
      clearSelected();
      latest.current.selectedAddress = undefined;
      setSelectedAddress(undefined);
      setSelectedProvider(undefined);
      return;
    }

    saveSelected(account.address.address, account.provider);
    latest.current.selectedAddress = account.address.address;
    setSelectedAddress(account.address.address);
    setSelectedProvider(account.provider);
  };

  const disconnect = (p: Provider.Key) => {
    manuallyTouched.current.add(p);
    Provider.signer(p).disconnect?.();
    removeProviderKey(p);
    updateAddresses(({ [p]: _, ...rest }) => rest);
  };

  const disconnectAll = () => {
    connectedProviders.forEach((provider) =>
      manuallyTouched.current.add(provider)
    );
    Object.keys(latest.current.addresses).map((a) =>
      Provider.signer(a as Provider.Key).disconnect?.()
    );
    clearProviderKeys();
    select();
    updateAddresses(() => ({}));
  };

  const selected = useMemo(
    () => findSelectedEntry(addresses, selectedAddress, selectedProvider),
    [selectedAddress, selectedProvider, addresses]
  );

  useEffect(() => {
    if (!selectedAddress || !selected || selected[0] === selectedProvider)
      return;
    const provider = selected[0];
    setSelectedProvider(provider);
    saveSelected(selectedAddress, provider);
  }, [selected, selectedAddress, selectedProvider]);

  const signer = (address: string): Signer => {
    const provider = pickSignerProvider(
      addresses,
      address,
      selectedAddress,
      selectedProvider
    );
    if (!provider) throw new Error(`No Signer found for ${address}`);
    return Provider.signer(provider);
  };

  const accounts = new Set(
    Object.entries(addresses).reduce(
      (a: { address: Address; provider: Provider.Key }[], [provider, v]) => [
        ...a,
        ...v.map((address) => ({
          address,
          provider: provider as Provider.Key,
        })),
      ],
      []
    )
  );

  const selectedAccount = selected?.[1].find(
    (a) => a.address === selectedAddress
  );

  const value: AccountProvider = {
    accounts: [...accounts],
    selected:
      selectedAddress && selected && selectedAccount
        ? {
            address: selectedAccount,
            provider: selected[0],
          }
        : undefined,
    select,
    connect,
    disconnect,
    disconnectAll,
    isAvailable: (p: Provider.Key) => Provider.signer(p).isAvailable(),
    signer,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

/**
 * The connected wallets and the wallet the user selected, for wallet-level
 * work: signing in, connecting, switching, depositing from or paying out to a
 * wallet, and anything a wallet signs.
 */
export const useWalletAccounts = (): AccountProvider => useContext(Context);

/**
 * The accounts as features see them: the connected wallets, and as `selected`
 * the account that acts for the selected wallet (itself, unless an account
 * layer puts one in front of it).
 */
export const useAccounts = (): Accounts => {
  const wallets = useContext(Context);
  const account = useAccountLayer()?.account;
  return useMemo(
    () =>
      account && wallets.selected
        ? {
            ...wallets,
            selected: {
              address: account.address,
              provider: wallets.selected.provider,
              owner: account.owner,
            },
          }
        : wallets,
    [account, wallets]
  );
};
